import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Button, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { usePoDetailContext } from 'providers/PoDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import useUpdate from 'hooks/useUpdate';
import useFetchById from 'hooks/useFetchById';
import useFileUpload from 'hooks/useFileUpload';

import IconButton from 'components/common/IconButton';
import SubtleBadge from 'components/common/SubtleBadge';
import KeyValueRow from 'components/purchase-order/KeyValueRow';
import PurchaseOrderForm from 'components/purchase-order/purchase-order/PurchaseOrderForm';

import { purchaseOrderApi } from 'api';
import { TIME_OPTION } from 'constants/time';
import { formatStatus } from 'utils/valueFormatters';
import { generatePOPdf } from 'utils/pdfGenerators';
import { getPurchaseOrderBadgeColor } from 'constants/purchaseOrder';

const PurchaseOrderDetailInfo = ({ listenToUpdates = false }) => {
  const { purchaseOrderId } = useParams();
  const { updatePoData } = usePoDetailContext();
  const { selectedWarehouse, warehouse_permissions } = useUserContext();

  const currentPermission = warehouse_permissions?.find(
    p => p.warehouse?.id === selectedWarehouse?.id,
  );
  const canApprovePO = currentPermission?.purchase_order >= 2;

  const [isEditing, setIsEditing] = useState(false);

  const [attachedFileUpdateLoading, setAttachedFileUpdateLoading] =
    useState(false);
  const [statusChangeRequestLoading, setStatusChangeRequestLoading] =
    useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { file, getRootProps, getInputProps, resetFile, clearState } =
    useFileUpload({
      fieldId: 'attached_file',
      accept: {},
    });

  // Get PO
  const {
    data: purchaseOrder,
    loading: fetchLoading,
    error: fetchError,
    setData: setPurchaseOrder,
    refetch: poRefetch,
  } = useFetchById(purchaseOrderApi, purchaseOrderId);

  useEffect(() => {
    if (purchaseOrder) {
      updatePoData(purchaseOrder);
    }
  }, [purchaseOrder, updatePoData]);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        poRefetch();
      };

      window.addEventListener('po-line-updated', handleUpdate);

      return () => window.removeEventListener('po-line-updated', handleUpdate);
    }
  }, [listenToUpdates, poRefetch]);

  const {
    update: poUpdate,
    loading: updateLoading,
    error: updateError,
  } = useUpdate(purchaseOrderApi, 'patch');

  // PO Update
  const handleSubmit = async values => {
    try {
      const response = await poUpdate(purchaseOrderId, values);

      setIsEditing(false);
      toast.success('PO updated successfully');

      await poRefetch();
    } catch (err) {
      toast.error('Failed to update PO');
    }
  };

  // File upload
  const handleFileUpload = async () => {
    setAttachedFileUpdateLoading(true);

    try {
      if (!file) {
        toast.error('Please select a file to upload');
        return;
      }

      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);

      if (parseFloat(fileSizeInMB) >= 10) {
        toast.error(
          `File size exceeds 10MB limit (Current size: ${fileSizeInMB}MB)`,
        );
        return;
      }

      const formData = new FormData();
      formData.append('attached_file', file);

      await purchaseOrderApi.updatePOFile(purchaseOrderId, formData);
      toast.success('File uploaded successfully');

      setShowUploadModal(false);
      clearState();

      await poRefetch();
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setAttachedFileUpdateLoading(false);
    }
  };

  // File delete
  const handleFileDelete = async () => {
    setAttachedFileUpdateLoading(true);

    try {
      const response = await purchaseOrderApi.updatePOFile(purchaseOrderId, {
        attached_file: null,
      });
      toast.success('File deleted successfully');

      setShowDeleteModal(false);

      await poRefetch();
    } catch (err) {
      toast.error('Failed to delete file');
    } finally {
      setAttachedFileUpdateLoading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    clearState();
  };

  const handleStatusChangeRequest = async status => {
    setStatusChangeRequestLoading(true);

    try {
      const response = await purchaseOrderApi.updatePOStatus(purchaseOrderId, {
        po_status: status,
      });

      if (status === 'approved') {
        toast.success('Purchase order has been approved successfully');
      } else if (status === 'pending') {
        toast.success('Approval request has been submitted successfully');
      }

      await poRefetch();
    } catch (err) {
      if (status === 'approved') {
        toast.error('Failed to approve purchase order');
      } else if (status === 'pending') {
        toast.error('Failed to submit approval request');
      }
    } finally {
      setStatusChangeRequestLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const isEditableStatus = status => {
    return status === 'draft' || status === 'pending';
  };

  const isPrintableStatus = status => {
    return (
      status === 'approved' ||
      status === 'in transit' ||
      status === 'done' ||
      status === 'discarded'
    );
  };

  const handlePrintPO = async () => {
    try {
      const response = await purchaseOrderApi.getById(purchaseOrderId);

      generatePOPdf(response);
    } catch (error) {
      toast.error('Error generating PDF', error);
    }
  };

  if (fetchLoading) {
    return (
      <Card className="h-100">
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{fetchError}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">PO #{purchaseOrder?.po_code}</h5>

          <SubtleBadge
            pill
            bg={getPurchaseOrderBadgeColor(purchaseOrder?.po_status)}
          >
            {formatStatus(purchaseOrder?.po_status)}
          </SubtleBadge>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-0 fs-9">PO #{purchaseOrder?.po_code}</h5>

            <div className="d-flex justify-content-end gap-2">
              {canApprovePO && purchaseOrder?.po_status === 'pending' && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={statusChangeRequestLoading}
                  onClick={() => handleStatusChangeRequest('approved')}
                >
                  Approve
                </Button>
              )}

              {purchaseOrder?.po_status === 'draft' &&
                purchaseOrder?.total_quantity > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={statusChangeRequestLoading}
                    onClick={() => handleStatusChangeRequest('pending')}
                  >
                    Request Approval
                  </Button>
                )}

              {isPrintableStatus(purchaseOrder?.po_status) &&
                purchaseOrder?.total_quantity > 0 &&
                purchaseOrder?.invoice_data && (
                  <IconButton
                    size="sm"
                    variant="falcon-default"
                    icon="file-alt"
                    onClick={handlePrintPO}
                  />
                )}
            </div>
          </div>

          <Row>
            <Col md={4}>
              <KeyValueRow label="Vendor" value={purchaseOrder?.vendor?.name} />

              <KeyValueRow
                label="Created"
                value={new Date(purchaseOrder?.created).toLocaleString(
                  'en-IN',
                  TIME_OPTION,
                )}
              />

              <KeyValueRow
                label="Updated"
                value={new Date(purchaseOrder?.modified).toLocaleString(
                  'en-IN',
                  TIME_OPTION,
                )}
              />

              {purchaseOrder?.attached_file ? (
                <div className="d-flex mb-2 fs-10">
                  <div
                    className="fw-semibold text-700"
                    style={{ width: '45%' }}
                  >
                    File
                  </div>
                  <div
                    className="text-black d-flex align-items-center"
                    style={{ width: '55%' }}
                  >
                    <a
                      href={purchaseOrder.attached_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black text-truncate me-2 text-decoration-underline"
                      style={{ maxWidth: '70%' }}
                    >
                      {purchaseOrder?.attached_file}
                    </a>
                    <FontAwesomeIcon
                      className="cursor-pointer"
                      icon="trash-alt"
                      onClick={() => setShowDeleteModal(true)}
                    />
                  </div>
                </div>
              ) : (
                <IconButton
                  variant="falcon-default"
                  size="sm"
                  onClick={() => setShowUploadModal(true)}
                  disabled={attachedFileUpdateLoading}
                  icon="file-upload"
                  transform="shrink-3"
                >
                  <span className="d-none d-sm-inline-block ms-1">
                    Add File
                  </span>
                </IconButton>
              )}
            </Col>

            <Col md={4}>
              <KeyValueRow
                label="Created By"
                value={purchaseOrder?.created_by?.email}
              />

              <KeyValueRow
                label="Approved By"
                value={purchaseOrder?.approved_by?.email}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {isEditing ? (
            <PurchaseOrderForm
              isCreate={false}
              isLoading={updateLoading}
              onSubmit={handleSubmit}
              onCancel={handleCancelEdit}
              purchaseOrder={purchaseOrder}
            />
          ) : (
            <div className="position-relative">
              <Row>
                <Col md={4}>
                  <h5 className="mb-3 fs-9">Basic Details</h5>

                  <KeyValueRow
                    label="PO Type"
                    value={purchaseOrder?.po_type?.toUpperCase()}
                  />

                  <KeyValueRow
                    label="Currency"
                    value={purchaseOrder?.currency}
                  />

                  <KeyValueRow
                    label="Expiration Date"
                    value={new Date(
                      purchaseOrder?.expiration_date,
                    ).toLocaleString('en-IN', TIME_OPTION)}
                  />
                </Col>

                <Col md={4}>
                  <h5 className="mb-3 fs-9">Logistic Details</h5>

                  <KeyValueRow
                    label="Shipping Type"
                    value={formatStatus(purchaseOrder?.shipping_type)}
                  />

                  <KeyValueRow
                    label="Shipping Charge"
                    value={purchaseOrder?.shipping_charge}
                  />

                  <KeyValueRow
                    label="Shipping Charge HSN Code"
                    value={purchaseOrder?.shipping_charge_hsn_code}
                  />

                  <KeyValueRow
                    label="Shipping Comments"
                    value={purchaseOrder?.shipping_comments}
                  />
                </Col>
              </Row>

              {!isEditing && isEditableStatus(purchaseOrder?.po_status) && (
                <Button
                  className="position-absolute top-0 end-0 w-auto"
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* File upload modal */}
      <Modal show={showUploadModal} onHide={handleCloseUploadModal}>
        <Modal.Header closeButton>
          <Modal.Title>File Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            {...getRootProps({
              className: 'border p-3 rounded text-center cursor-pointer',
            })}
          >
            <input {...getInputProps()} />
            <p className="mb-0">
              {file ? file.name : 'Drop your file here or click and select'}
            </p>
          </div>
          {file && (
            <div className="mt-2 text-center">
              <Button
                variant="link"
                className="text-danger p-0"
                onClick={resetFile}
              >
                Remove File
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseUploadModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleFileUpload}
            disabled={!file || attachedFileUpdateLoading}
          >
            {attachedFileUpdateLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File delete modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>File Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Are you sure you want to delete this file?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleFileDelete}
            disabled={attachedFileUpdateLoading}
          >
            {attachedFileUpdateLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PurchaseOrderDetailInfo;
